import api from '@/lib/api/axios'
import type { ForensicsEnvironment, ForensicsObjectVersion } from '@/types/entities'

// ── New forensics REQUEST / SESSION contract (mirrors Request Recovery) ──────────────
// Mode is stored raw; the UI maps it to a label (see MODE_LABELS) and never shows the raw value.
export type ForensicsMode = 'disk' | 'single' | 'multi'

export interface ForensicsConnectivityLink {
    type: string            // "Forensic Workstation (DCV)" | "Forensics Instance" | …
    url?: string
    expires?: string
    instance_id?: string
    private_ip?: string
    /** DCV only: the session the URL's fragment must name. */
    session_id?: string
    /** "orchestrator-token" means the desktop authenticates against this application. */
    auth?: string
}

/** A minted, single-use DCV token and everything needed to build the desktop URL. */
export interface ForensicsConnectToken {
    token: string
    url: string
    sessionId: string
    expiresAt: string
}

export interface ForensicsRequest {
    id: number
    objectIds: string[]
    vaultId: string | null
    requestedBy: string | null
    status: string          // Pending → SGCreated → CopyRequested → Copying → Ready → Expired/Error
    mode: ForensicsMode | null
    expiryTime: string | null
    connectivityLinks: ForensicsConnectivityLink[]
    created: string | null
    updated: string | null
    environment?: {
        id: number
        status: string
        computeInstances: unknown[]
        expiryTime: string | null
    } | null
}

export interface RunningForensicsEnvironment {
    environmentId: number
    requestId: number
    vaultId: string | null
    objectIds: string[]
    computeInstances: unknown[]
    expiryTime: string | null
    status: string
}

export type ForensicsToolType = 'yara' | 'sigma' | 'script' | 'package' | 'binary'

export interface ForensicsTool {
    id: number
    vaultId: string | null
    name: string
    toolType: ForensicsToolType
    content: string | null
    createdBy: string | null
    createdAt: string | null
}

/** A live forensics environment in a vault — what the dialog opens on.
 *
 *  Scoped by VAULT, not by object: an object's versions can live in different vaults, so an
 *  environment listed for the wrong vault could never hold the selected version.
 */
export interface ForensicsVaultEnvironment {
    requestId: number
    environmentId: number | null
    vaultId: string
    mode: string | null
    status: string
    expiryTime: string | null
    created: string | null
    requestedBy: string | null
    contents: { versionName: string; objectId: string | null }[]
    instanceCount: number
    workstationUrl: string | null
    /** Ready AND has a workstation link — anything else cannot be opened. */
    openable: boolean
    /** Always false today; joinBlockedReason says why, so the UI can explain rather than hide. */
    joinable: boolean
    joinBlockedReason: string
}

/** An object a forensics workstation can mount, from vw_ForensicsExaminableObjects. */
export interface ForensicsExaminableObject {
    ObjectID: string
    Description: string
    Classification: string | null
    /** Display only. NOTHING gates on this — the disk image is the signal. */
    Category: string | null
    /** How much of the object's history can be opened. Often less than TotalVersions. */
    MountableVersions: number
    TotalVersions: number
    /** Distinct disk formats across the mountable history, e.g. ['vhdx']. */
    Formats: string[]
}

/** A mountable version, from vw_ForensicsExaminableVersions. */
export interface ForensicsExaminableVersion {
    versionID: number
    objectName: string
    objectPrimary: string
    objectSecondary: string
    vaultDatetime: string | null
    sizeMB: number | null
    anomalyScore: number | null
    sourceFormat: string
    /** 'metadata' = the agent declared it, 'filelist' = we inferred it from the file names. */
    formatSource: 'metadata' | 'filelist' | 'none'
}

export const forensicsApi = {
    /**
     * Mint the one-time token for a workstation desktop.
     *
     * Deliberately NOT called until the moment of connecting: it lives for 60 seconds, so
     * fetching it with the page would hand the user an expired link by the time they clicked.
     */
    connectToken: (requestId: number) =>
        api.post<{ success: boolean; data: ForensicsConnectToken }>(`/api/forensics/requests/${requestId}/connect-token`, {}),

    listEnvironments: (params?: { vaultId?: number }) =>
        api.get<ForensicsEnvironment[]>('/api/forensics/environments', { params }),

    getEnvironment: (id: number) =>
        api.get<ForensicsEnvironment>(`/api/forensics/environments/${id}`),

    createEnvironment: (data: {
        name: string
        vaultId: number
        securityGroupId: string
        awsAccountId: string
        description?: string
        servers?: Array<{ computeObjectId: string; computeObjectVersionId?: number }>
    }) => api.post<ForensicsEnvironment>('/api/forensics/environments', data),

    updateEnvironment: (id: number, data: {
        name?: string
        securityGroupId?: string
        awsAccountId?: string
        description?: string
        status?: string
        servers?: Array<{ computeObjectId: string; computeObjectVersionId?: number }>
    }) => api.put<ForensicsEnvironment>(`/api/forensics/environments/${id}`, data),

    deleteEnvironment: (id: number) =>
        api.delete(`/api/forensics/environments/${id}`),

    listVersions: (envId: number) =>
        api.get<ForensicsObjectVersion[]>(`/api/forensics/environments/${envId}/versions`),

    startVersion: (envId: number, data: { objectId: string; objectVersionId: number }) =>
        api.post<ForensicsObjectVersion>(`/api/forensics/environments/${envId}/versions`, data),

    stopVersion: (envId: number, versionId: number) =>
        api.delete(`/api/forensics/environments/${envId}/versions/${versionId}`),

    /**
     * Objects a forensics workstation can actually mount.
     *
     * GATED ON HAVING A DISK IMAGE, not on classification category. The previous gate asked for
     * Category = 'Compute' and got it backwards — 68 objects offered of which 2 were mountable,
     * and every domain controller that was mountable hidden, because a DC is classified by what
     * it does rather than what it is.
     */
    listComputeObjects: () =>
        api.get<ForensicsExaminableObject[]>('/api/forensics/compute-objects'),

    /**
     * The versions of one object that can be mounted. NOT every version: 9 of the 11 objects with
     * mountable history carry a mix — AD1 has 116 of 139 — so offering all of them lets an
     * investigator pick a version with nothing to attach.
     */
    listComputeObjectVersions: (objectId: string) =>
        api.get<ForensicsExaminableVersion[]>(
            `/api/forensics/compute-objects/${encodeURIComponent(objectId)}/versions`,
        ),

    // ── New request/session contract ─────────────────────────────────────────────────
    // Active (non-expired/-error) sessions referencing an object.
    listRequests: (objectId?: string) =>
        api.get<ForensicsRequest[]>('/api/forensics/requests', { params: objectId ? { objectId } : undefined }),

    // Create a new environment request (Status="Pending"); the forensics account provisions it.
    createRequest: (data: { objectIds: string[]; mode: ForensicsMode; vaultId: string | number; toolIds?: number[] }) =>
        api.post<ForensicsRequest>('/api/forensics/requests', data),

    // Poll target: status + connectivity links (+ provisioned environment).
    getRequest: (id: number) =>
        api.get<ForensicsRequest>(`/api/forensics/requests/${id}`),

    // Running MULTI-NODE environments a user can JOIN.
    listRunningEnvironments: (vaultId?: string | number) =>
        api.get<RunningForensicsEnvironment[]>('/api/forensics/running-environments', {
            params: vaultId != null ? { vaultId } : undefined,
        }),

    // Live environments in a VAULT — what the Forensics dialog opens on.
    vaultEnvironments: (vaultId: string | number) =>
        api.get<{ success: boolean; data: ForensicsVaultEnvironment[] }>(
            `/api/forensics/vault-environments?vaultId=${encodeURIComponent(String(vaultId))}`),

    // JOIN a running multi-node environment (sends the ForensicsJoin SQS command server-side).
    join: (data: { objectIds: string[]; joinEnvironmentId: number; vaultId?: string | number }) =>
        api.post('/api/forensics/join', data),

    // ── Customer custom tools ────────────────────────────────────────────────────────
    listTools: (vaultId?: string | number) =>
        api.get<ForensicsTool[]>('/api/forensics/tools', { params: vaultId != null ? { vaultId } : undefined }),
    addTool: (data: { vaultId?: string | number; name: string; toolType: ForensicsToolType; content?: string }) =>
        api.post<ForensicsTool>('/api/forensics/tools', data),
    removeTool: (id: number) =>
        api.delete(`/api/forensics/tools/${id}`),
}
